import { apiV1, apiV2 } from "@/store";
import { cloneDeep } from "lodash";
// eslint-disable-next-line
import { EventBus } from "@/Event";
import axios from "axios";
import { findGlobalSetting } from "@/helpers/find-global-setting";
import moment from "moment";

const defaultState = {
  changingTenant: false,
  info: {
    first_name: "",
    last_name: "",
    street_address: "",
    city: "",
    state: "",
    postal_code: "",
    phone_number: "",
    email: "",
    id_number: "",
    id_type: "",
    id_state: "",
    external_id: "",
    ledger_id: "",
    access_code: "",
    waiting_id: "",
    is_business: false,
    business_name: ""
  },
  companyInfo: {
    business_name: "",
    is_business: false
  },
  options: {
    followupDate: moment().format("YYYY-MM-DD"),
    moveInDate: moment().format("YYYY-MM-DD"),
    reservationType: "",
    selectedPromotion: null,
    tenantProtection: "",
    automaticPayments: "1",
    leadConversion: false,
    giftCardEligible: false
  },
  lease: {
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_email: "",
    military_contact: "",
    military_contact_phone: "",
    lien_holders: "",
    personal_property_types: [],
    personal_property_other: ""
  },
  creditCard: {
    firstName: "",
    lastName: "",
    billingAddress: "",
    billingState: "",
    billingZip: "",
    ccNumber: "",
    ccType: "",
    ccExpirationMonth: "",
    ccExpirationYear: "",
    ccCVV: "",
    useExistingInfo: false
  },
  payment: {
    balanceDetails: [],
    cart: []
  },
  ledgers: [],
  arPastDue: 0,
  salesPastDue: 0,
  reservationMax: 7
};

export const state = cloneDeep(defaultState);

export const actions = {
  findTenant: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${payload.id}/tenants`, {
        params: {
          first_name: payload.tenant.first_name,
          last_name: payload.tenant.last_name,
          phone: payload.tenant.phone,
          email: payload.tenant.email,
          access_code: payload.tenant.access_code,
          unit_number: payload.tenant.unit_number,
          select: [
            "access_code",
            "business_name",
            "email",
            "facility_id",
            "first_name",
            "is_active",
            "last_name",
            "phone_number",
            "pms_tenant_id"
            // "unit_number" // currently un-implemented in v2
          ],
          with: ["facility:id,location_code,name,street_address"]
        }
      })
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  globalFindTenant: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`tenant-search`, {
        params: {
          first_name: payload.tenant.first_name,
          last_name: payload.tenant.last_name,
          phone: payload.tenant.phone,
          email: payload.tenant.email,
          access_code: payload.tenant.access_code,
          select: [
            "is_active",
            "facility_id",
            "first_name",
            "last_name",
            "access_code",
            "email",
            "pms_tenant_id",
            "business_name",
            "phone_number"
            // @todo - get facility_unit_id from API
          ],
          with: ["facility:id,location_code,name,street_address,pms_enabled"]
        }
      })
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getTenantInfo: ({ commit, state, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      commit("smsOptIn/resetSmsOptIn", { root: true });
      apiV2.get(`facilities/${payload.facilityId}/tenants/${payload.tenantId}`, {
        params: {
          // Without select params, returns some unused values such as lat/long, alt phones, etc
          select: [
            "pms_tenant_id",
            "first_name",
            "last_name",
            "phone_number",
            "email",
            "street_address",
            "street_address_2",
            "city",
            "state",
            "postal_code",
            "id_number",
            "id_type",
            "id_state",
            "is_business",
            "business_name",
            "access_code"
          ]
        }
      })
        .then((resp) => {
          commit("updateInfo", resp.data);
          if (state.info && state.info.phone_number) {
            dispatch("smsOptIn/get", state.info.phone_number);
          }
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getTenantReservations: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${payload.facility_id}/tenants/${payload.tenant_id}/reservations`, {
        params: {
          with: [
            "promotion"
          ]
        }
      })
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getLedgerInfo: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/ledgers`, {
        params: {
          with: ["facilityUnit"]
        }
      })
        .then((resp) => {
          commit("setLedgers", resp.data);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getLedgerStatement: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/ledgers/${payload.ledgerId}/statement`, {
        params: {
          select: [
            "type",
            "description",
            "amount",
            "tax",
            "date"
          ]
        }
      })
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getPaymentHistory: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/ledgers/${payload.ledgerId}/payment-history`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getBalanceDetails: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/balance-details`)
        .then((resp) => {
          const payload = { key: "balanceDetails", value: resp.data };
          commit("updatePaymentValue", payload);
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  updateTenantInfo: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.patch(`facilities/${payload.facilityId}/tenants/${payload.tenantId}`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  updateBillingPreferences: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.patch(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/ledgers/${payload.ledgerId}/autopay`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getLedgerNotes: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.get(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/ledgers/${payload.ledgerId}/notes`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  createLedgerNote: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(
        `facilities/${payload.facilityId}/tenants/${payload.tenantId}/ledgers/${payload.ledgerId}/notes`,
        {
          description: payload.description,
          notify_store_manager: (
            payload.notify_store_manager
              ? payload.notify_store_manager
              : false
          )
        }
      )
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  convenienceFeeLastWaived: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.get(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/waived-convenience-fee`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  waiveConvenienceFee: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV1.post(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/waive-convenience-fee`)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  scheduleMoveOut: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.post(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/intent-to-vacate/${payload.ledgerId}`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  makePayment: ({}, payload) => {
    return new Promise((resolve, reject) => {
      apiV2.patch(`facilities/${payload.facilityId}/tenants/${payload.tenantId}/make-payment`, payload)
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  getAllGlobalSettings: ({ commit }) => {
    return new Promise((resolve, reject) => {
      axios.create({
        baseURL: process.env.VUE_APP_API_ALT_URL
      }).get(`/global-settings`)
        .then((resp) => {
          const globalSettings = resp.data.global_settings;
          commit("setArPastDue", findGlobalSetting(globalSettings, "global_cc_ar_past_due"));
          commit("setSalesPastDue", findGlobalSetting(globalSettings, "global_cc_sales_past_due"));
          commit("setReservationMax", findGlobalSetting(globalSettings, "global_reservation_lead_max"));
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  sendQuickPay: ({ dispatch }, payload) => {
    return axios.create({
      baseURL: process.env.VUE_APP_API_V2_ALT_URL
    }).post(`/quick-pay/facilities/${payload.facility_id}/tenants/${payload.tenant_id}/send-link`, payload)
      .then((resp) => {
        dispatch("handleSuccess", "QuickPay link successfully sent", { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("handleError", err, { root: true });
        return Promise.reject(err);
      });
  },

  handleTenantCompanyInfo: ({ commit }, payload) => {
    commit("updateTenantCompanyInfo", payload);
  },

  handleResetCompanyInfo: ({ commit }) => {
    commit("resetCompanyInfo");
  },

  handleResetTenant: ({ commit }) => {
    commit("resetTenant");
  }
};

export const mutations = {
  changingTenant: (state, status) => {
    state.changingTenant = status;
  },

  // Top-level objects
  updateInfo: (state, data) => {
    if (data === null) {
      state.info = cloneDeep(defaultState.info);
    } else {
      state.info.first_name = data.first_name; // state.info.sFName
      state.info.last_name = data.last_name; // state.info.sLName
      state.info.street_address = data.street_address; // state.info.sAddr1
      state.info.street_address_2 = data.street_address_2; // didn't exist?
      state.info.city = data.city; // state.info.sCity
      state.info.state = data.state; // state.info.sRegion
      state.info.postal_code = data.postal_code; // state.info.sPostalCode
      state.info.phone_number = data.phone_number; // state.info.sPhone
      state.info.email = data.email; // state.info.sEmail
      state.info.id_number = data.id_number; // state.info.sLicense
      state.info.id_type = data.id_type;
      state.info.id_state = data.id_state;
      state.info.external_id = data.pms_tenant_id || data.external_id; // state.info.tenant_id
      state.info.ledger_id = "data.ledger_id"; // @todo FIX
      state.info.access_code = data.access_code;
      state.info.waiting_id = data.waiting_id ? data.waiting_id : null; // @todo FIX
      state.info.is_business = !!data.is_business; // state.info.bCompanyIsTenant
      state.info.business_name = data.business_name; // state.info.sCompany
    }
  },
  setLedgers: (state, ledgers) => {
    state.ledgers = ledgers;
  },

  // Info values
  updateInfoValue: (state, payload) => {
    state.info[payload.key] = payload.value;
  },
  updateFirstName: (state, firstName) => {
    state.info.first_name = firstName;
  },
  updateLastName: (state, lastName) => {
    state.info.last_name = lastName;
  },
  updateReservationType: (state, reservationType) => {
    state.options.reservationType = reservationType;
  },
  updateAddress: (state, address) => {
    state.info.street_address = address;
  },
  updateCity: (state, city) => {
    state.info.city = city;
  },
  updateState: (state, region) => {
    state.info.state = region;
  },
  updatePostalCode: (state, postalCode) => {
    state.info.postal_code = postalCode;
  },
  updatePhone: (state, phone) => {
    state.info.phone_number = phone;
  },
  updateEmail: (state, email) => {
    state.info.email = email;
  },
  updateIdNumber: (state, idNumber) => {
    state.info.id_number = idNumber;
  },
  updateIdType: (state, idType) => {
    state.info.id_type = idType;
  },
  updateIdState: (state, idState) => {
    state.info.id_state = idState;
  },
  updateIsBusinessCustomer: (state, isBusinessCustomer) => {
    state.info.is_business = isBusinessCustomer;
  },
  updateCompanyName: (state, companyName) => {
    if (!state.info.bCompanyIsTenant) {
      state.info.business_name = null;
    };
    state.info.business_name = companyName;
  },
  updateTenantId: (state, tenantId) => {
    state.info.external_id = tenantId;
  },
  updateLedgerId: (state, ledgerId) => {
    state.info.ledger_id = ledgerId;
  },
  updateTenantCompanyInfo: (state, companyInfo) => {
    state.companyInfo.business_name = state.info.business_name ? state.info.business_name : companyInfo.business_name;
    state.companyInfo.is_business = state.info.is_business
      ? state.info.is_business
      : companyInfo.is_business;
  },

  // Options Values
  updateOptionsValue: (state, payload) => {
    state.options[payload.key] = payload.value;
  },
  updateMoveInDate: (state, moveInDate) => {
    state.options.moveInDate = moveInDate;
  },
  updateFollowupDate: (state, followupDate) => {
    state.options.followupDate = followupDate;
  },
  updatePromotion: (state, selectedPromotion) => {
    state.options.selectedPromotion = selectedPromotion;
  },
  updateTenantProtection: (state, tenantProtection) => {
    state.options.tenantProtection = tenantProtection;
  },
  setAutomaticPayments: (state, automaticPayments) => {
    state.options.automaticPayments = automaticPayments;
  },
  setLeadConversion: (state) => {
    state.options.leadConversion = true;
  },

  // Lease Values
  updateECName: (state, ecName) => {
    state.lease.emergency_contact_name = ecName;
  },
  updateECPhone: (state, ecPhone) => {
    state.lease.emergency_contact_phone = ecPhone;
  },
  updateECEmail: (state, ecEmail) => {
    state.lease.emergency_contact_email = ecEmail;
  },
  updateMCName: (state, mcName) => {
    state.lease.military_contact = mcName;
  },
  updateMCPhone: (state, mcPhone) => {
    state.lease.military_contact_phone = mcPhone;
  },
  updateLienHolders: (state, lienHolders) => {
    state.lease.lien_holders = lienHolders;
  },
  updatePropertyTypes: (state, propertyTypes) => {
    state.lease.personal_property_types = propertyTypes;
  },
  updatePropertyOther: (state, propertyOther) => {
    state.lease.personal_property_other = propertyOther;
  },

  // Credit Card values
  updateCCValue: (state, payload) => {
    state.creditCard[payload.key] = payload.value;
  },
  autofillCreditCardInfo: (state, cardInfo) => {
    state.useExistingInfo = true;
    state.creditCard.firstName = cardInfo.firstName;
    state.creditCard.lastName = cardInfo.lastName;
    state.creditCard.billingAddress = cardInfo.billingAddress;
    state.creditCard.billingState = cardInfo.billingState;
    state.creditCard.billingZip = cardInfo.billingZip;
  },
  setCCFirstName: (state, firstName) => {
    state.creditCard.firstName = firstName;
  },
  setCCLastName: (state, lastName) => {
    state.creditCard.lastName = lastName;
  },
  setCCBillingAddress: (state, billingAddress) => {
    state.creditCard.billingAddress = billingAddress;
  },
  setCCBillingState: (state, billingState) => {
    state.creditCard.billingState = billingState;
  },
  setCCBillingZip: (state, billingZip) => {
    state.creditCard.billingZip = billingZip;
  },
  setCCNumber: (state, ccNumber) => {
    state.creditCard.ccNumber = ccNumber;
  },
  setCCType: (state, ccType) => {
    state.creditCard.ccType = ccType;
  },
  setCCExpirationMonth: (state, ccExpirationMonth) => {
    state.creditCard.ccExpirationMonth = ccExpirationMonth;
  },
  setCCExpirationYear: (state, ccExpirationYear) => {
    state.creditCard.ccExpirationYear = ccExpirationYear;
  },
  setCCCvv: (state, ccCVV) => {
    state.creditCard.ccCVV = ccCVV;
  },
  clearCreditCard: (state) => {
    EventBus.$emit("ccCleared");
    state.creditCard = {
      firstName: "",
      lastName: "",
      billingAddress: "",
      billingState: "",
      billingZip: "",
      ccNumber: "",
      ccType: "",
      ccExpirationMonth: "",
      ccExpirationYear: "",
      ccCVV: "",
      useExistingInfo: false
    };
  },

  // Payment
  updatePaymentValue: (state, payload) => {
    state.payment[payload.key] = payload.value;
  },

  setGiftCardEligible: (state, value) => {
    state.options.giftCardEligible = value;
  },

  // Past Due Date
  setArPastDue: (state, dateLimit) => {
    state.arPastDue = dateLimit;
  },

  setSalesPastDue: (state, dateLimit) => {
    state.salesPastDue = dateLimit;
  },

  setReservationMax: (state, dateLimit) => {
    state.reservationMax = dateLimit;
  },

  resetCompanyInfo: (state) => {
    state.companyInfo = {
      business_name: "",
      is_business: false
    };
  },

  // Clear state
  resetTenant: (state) => {
    state.info = {
      first_name: "",
      last_name: "",
      street_address: "",
      city: "",
      state: "",
      postal_code: "",
      phone_number: "",
      email: "",
      id_number: "",
      id_type: "",
      id_state: "",
      external_id: "",
      ledger_id: "",
      access_code: "",
      waiting_id: "",
      is_business: false,
      business_name: ""
    };
    state.companyInfo = {
      business_name: "",
      is_business: false
    };
    state.options = {
      followupDate: moment().format("YYYY-MM-DD"),
      moveInDate: moment().format("YYYY-MM-DD"),
      reservationType: "",
      selectedPromotion:
        typeof state.options !== "undefined" &&
        typeof state.options.selectedPromotion !== "undefined"
          ? state.options.selectedPromotion
          : "",
      tenantProtection: "",
      automaticPayments: "1",
      leadConversion: false,
      giftCardEligible: false
    };
    state.lease = {
      emergency_contact_name: "",
      emergency_contact_phone: "",
      emergency_contact_email: "",
      military_contact: "",
      military_contact_phone: "",
      lien_holders: "",
      personal_property_types: [],
      personal_property_other: ""
    };
    state.creditCard = {
      firstName: "",
      lastName: "",
      billingAddress: "",
      billingState: "",
      billingZip: "",
      ccNumber: "",
      ccType: "",
      ccExpirationMonth: "",
      ccExpirationYear: "",
      ccCVV: "",
      useExistingInfo: false
    };
    state.payment = {
      balanceDetails: [],
      cart: []
    };
    state.ledgers = [];
  }
};

export default {
  name: "tenant",
  state,
  actions,
  mutations
};
